import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@heroui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import cn from 'classnames';
import { Svg } from '@/shared/ui/Svg';
import { AppButton } from '@/shared/ui/AppButton';
import { Container } from '@/shared/ui/Container';
import { useForgotPasswordMutation } from '@/features/Auth/api/authApi';
import { useToast } from '@/shared/lib/hooks';
import { routerPath } from '@/shared/config/router';
import LogoIcon from '@/shared/assets/icons/complex/logo/logo-black.complex.svg';
import styles from './ForgotPasswordView.module.scss';

interface ForgotPasswordFormData {
  email: string;
}

const forgotPasswordSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
});

export const ForgotPasswordView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [emailSent, setEmailSent] = useState(false);
  
  const token = searchParams.get('token');
  
  // Если есть токен в URL, перенаправляем на страницу сброса пароля
  useEffect(() => {
    if (token) {
      navigate(`${routerPath.resetPassword}?token=${token}`, { replace: true });
    }
  }, [token, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await forgotPassword({ email: data.email }).unwrap();
      setEmailSent(true);
      toast({
        title: 'Success',
        description: 'Password reset email sent. Please check your inbox.',
        color: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to send reset email',
        color: 'danger',
      });
    }
  };

  if (emailSent) {
    return (
      <div className={cn(styles.root)}>
        <Container className={styles.container}>
          <div className={styles.wrapper}>
            <Svg className={styles.logo} Icon={LogoIcon} />
            <h1 className={styles.title}>Password recovery</h1>
            <div className={styles.form}>
              <p className={styles.message}>
                Password reset link has been sent to your email. Please check your inbox and click
                the link to reset your password.
              </p>
              <div className={styles.successIcon}>
                <div className={styles.checkmark}>✓</div>
              </div>
              <AppButton
                className={styles.button}
                color="grey"
                onPress={() => navigate(routerPath.login)}
              >
                Back to Sign in
              </AppButton>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={cn(styles.root)}>
      <Container className={styles.container}>
        <div className={styles.wrapper}>
          <Svg className={styles.logo} Icon={LogoIcon} />
          <h1 className={styles.title}>Password recovery</h1>
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className={styles.inputContainer}>
              <Input
                isRequired
                type="email"
                label="E-mail"
                labelPlacement="outside"
                placeholder="Enter your E-mail"
                errorMessage={errors.email?.message}
                variant="bordered"
                radius="sm"
                classNames={{
                  inputWrapper: cn(
                    styles.inputWrapper,
                    errors.email
                      ? 'border-red-500 focus-within:border-red-500'
                      : 'border-[#E5E0EB] hover:border-black-DEFAULT focus-within:border-black-DEFAULT',
                  ),
                  label: 'pr-0 text-sm leading-[1.4] font-normal text-black-secondary',
                }}
                {...register('email')}
              />
            </div>
            <AppButton type="submit" className={styles.button} color="grey" disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Confirm E-mail'}
            </AppButton>
            <button
              type="button"
              className={styles.backLink}
              onClick={() => navigate(routerPath.login)}
            >
              Back to Sign in
            </button>
          </form>
        </div>
      </Container>
    </div>
  );
};
